/**
 * ConcernsPanel.jsx
 * 
 * Displays flagged health issues for a specific patient in the PatientDetail page.
 * Features severity color coding, resolution workflow, and linked conversations.
 * 
 * Usage:
 *   <ConcernsPanel patientId="PT001" onResolve={handleResolve} />
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { 
  AlertCircle, 
  AlertTriangle,
  Check, 
  Clock, 
  ChevronDown,
  ChevronRight,
  MessageSquare,
  User,
  Calendar,
  Info,
  RefreshCw
} from 'lucide-react'
import { mdApi } from '../api/mdApi'
import './ConcernsPanel.css'

// ============================================================================
// Constants
// ============================================================================

/**
 * Severity configuration with colors and icons
 * Following the clinical color coding from the implementation plan
 */
const SEVERITY_CONFIG = {
  urgent: { 
    label: 'Urgent', 
    color: '#DC3545', 
    bgColor: '#FEE2E2',
    textColor: '#991B1B',
    icon: '🔴',
    description: 'Immediate attention required'
  },
  high: { 
    label: 'High', 
    color: '#FD7E14', 
    bgColor: '#FEF3C7',
    textColor: '#92400E',
    icon: '🟠',
    description: 'Same-day attention needed'
  },
  medium: { 
    label: 'Medium', 
    color: '#FFC107', 
    bgColor: '#FEF9C3',
    textColor: '#854D0E',
    icon: '🟡',
    description: 'Monitor closely'
  },
  low: { 
    label: 'Low', 
    color: '#28A745', 
    bgColor: '#D1FAE5',
    textColor: '#065F46',
    icon: '🟢',
    description: 'Routine follow-up'
  },
}

/**
 * Resolution options for the dropdown
 */
const RESOLUTION_OPTIONS = [
  { value: 'resolved', label: 'Resolved', description: 'Issue has been addressed' },
  { value: 'already_known', label: 'Already Known', description: 'Condition is already in patient file' },
  { value: 'escalated', label: 'Escalated', description: 'Referred for further evaluation' },
  { value: 'not_applicable', label: 'Not Applicable', description: 'False positive or not relevant' },
  { value: 'monitoring', label: 'Monitoring', description: 'Will continue to observe' },
]

/**
 * Concern type labels for display
 */
const CONCERN_TYPE_LABELS = {
  new_symptom: 'New Symptom',
  medication_issue: 'Medication Issue',
  vital_sign: 'Vital Sign Anomaly',
  pain: 'Pain Report',
  mental_health: 'Mental Health',
  adherence: 'Adherence Concern',
  emergency: 'Emergency Flag',
  other: 'Other',
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format relative time with overdue detection
 * @param {string} dateString - ISO date string
 * @returns {Object} { text: string, isOverdue: boolean }
 */
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  const isOverdue = diffHours >= 24

  if (diffMins < 1) {
    return { text: 'Just now', isOverdue: false }
  } else if (diffMins < 60) {
    return { text: `${diffMins} min ago`, isOverdue: false }
  } else if (diffHours < 24) {
    return { text: `${diffHours}h ago`, isOverdue: false }
  } else {
    return { text: `${diffDays} days ago`, isOverdue }
  }
}

// ============================================================================
// Sub-Components
// ============================================================================

/**
 * ConcernCard - Individual concern display with resolution workflow
 */
const ConcernCard = ({ 
  concern, 
  isExpanded, 
  onToggleExpand, 
  onResolve,
  onViewConversation,
  onDiscussWithLuna
}) => {
  const [selectedResolution, setSelectedResolution] = useState('')
  const [notes, setNotes] = useState('')
  const [isResolving, setIsResolving] = useState(false)
  const [error, setError] = useState(null)
  
  const config = SEVERITY_CONFIG[concern.severity] || SEVERITY_CONFIG.medium
  const timeInfo = formatRelativeTime(concern.created_at)
  const concernTypeLabel = CONCERN_TYPE_LABELS[concern.concern_type] || concern.concern_type
  
  /**
   * Handle resolution submission
   * Note: We use concern_id (string like "CONCERN-xxx") for the API call,
   * which is the unique identifier the backend uses to look up concerns.
   */
  const handleResolve = async (resolutionStatus) => {
    if (!resolutionStatus) return
    
    setIsResolving(true)
    setError(null)
    
    try {
      // Use concern_id (the string ID) for the API call, not the numeric id
      const idForApi = concern.concern_id || concern.id
      await onResolve(idForApi, {
        status: resolutionStatus,
        notes: notes.trim(),
        resolved_by: 'Current User', // In production, get from auth context
      })
    } catch (err) {
      setError('Failed to resolve concern. Please try again.')
      console.error('Error resolving concern:', err)
    } finally {
      setIsResolving(false)
    }
  }
  
  /**
   * Quick acknowledge button handler
   */
  const handleQuickAcknowledge = () => {
    handleResolve('acknowledged')
  }
  
  return (
    <div 
      className={`concern-card severity-${concern.severity}`}
      style={{ 
        '--severity-color': config.color,
        '--severity-bg': config.bgColor,
        '--severity-text': config.textColor,
      }}
    >
      {/* Card Header */}
      <div 
        className="concern-card-header"
        onClick={() => onToggleExpand(concern.id)}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => e.key === 'Enter' && onToggleExpand(concern.id)}
      >
        <div className="concern-severity-badge">
          <span className="severity-icon">{config.icon}</span>
          <span className="severity-text">{config.label.toUpperCase()}</span>
        </div>
        
        <div className="concern-main-info">
          <div className="concern-description">
            {concern.description || concernTypeLabel}
          </div>
          <div className="concern-type-badge">
            {concernTypeLabel}
          </div>
        </div>
        
        <div className="concern-header-right">
          <span className={`concern-time ${timeInfo.isOverdue ? 'overdue' : ''}`}>
            <Clock size={12} />
            {timeInfo.text}
            {timeInfo.isOverdue && ' - OVERDUE'}
          </span>
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </span>
        </div>
      </div>
      
      {/* Known vs New Indicator */}
      {concern.is_known_condition !== undefined && (
        <div className="known-indicator">
          {concern.is_known_condition ? (
            <span className="known">
              <Info size={12} />
              Known condition - already in patient file
            </span>
          ) : (
            <span className="new">
              <AlertCircle size={12} />
              New finding - not previously documented
            </span>
          )}
        </div>
      )}
      
      {/* Expanded Details */}
      {isExpanded && (
        <div className="concern-details">
          {/* AI Recommendation */}
          {concern.ai_recommendation && (
            <div className="ai-recommendation-box">
              <h4>
                <AlertTriangle size={14} />
                AI Recommendation
              </h4>
              <p>{concern.ai_recommendation}</p>
            </div>
          )}
          
          {/* Additional Context */}
          <div className="concern-context">
            {concern.context && (
              <div className="context-item">
                <strong>Context:</strong> {concern.context}
              </div>
            )}
            
            <div className="context-item">
              <Calendar size={12} />
              Detected: {new Date(concern.created_at).toLocaleString()}
            </div>
            
            {concern.conversation_id && (
              <button 
                className="btn-view-conversation"
                onClick={(e) => {
                  e.stopPropagation()
                  onViewConversation(concern.conversation_id)
                }}
              >
                <MessageSquare size={12} />
                View Source Conversation
              </button>
            )}
            
            {onDiscussWithLuna && (
              <button 
                className="btn-discuss-luna"
                onClick={(e) => {
                  e.stopPropagation()
                  onDiscussWithLuna(concern)
                }}
              >
                <MessageSquare size={12} />
                Chat about Symptom
              </button>
            )}
          </div>
          
          {/* Resolution Workflow */}
          <div className="resolution-section">
            <h4>Resolution</h4>
            
            {error && (
              <div className="resolution-error">
                <AlertCircle size={14} />
                {error}
              </div>
            )}
            
            <div className="resolution-controls">
              {/* Quick Acknowledge Button */}
              <button 
                className="btn-acknowledge"
                onClick={handleQuickAcknowledge}
                disabled={isResolving}
              >
                <Check size={14} />
                {isResolving ? 'Processing...' : 'Acknowledge'}
              </button>
              
              {/* Resolution Dropdown */}
              <div className="resolution-dropdown">
                <select
                  value={selectedResolution}
                  onChange={(e) => setSelectedResolution(e.target.value)}
                  disabled={isResolving}
                  aria-label="Select resolution status"
                >
                  <option value="">Select resolution...</option>
                  {RESOLUTION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Apply Resolution Button */}
              <button
                className="btn-apply-resolution"
                onClick={() => handleResolve(selectedResolution)}
                disabled={!selectedResolution || isResolving}
              >
                Apply
              </button>
            </div>
            
            {/* Resolution Notes */}
            <div className="resolution-notes">
              <label htmlFor={`notes-${concern.id}`}>Notes (optional):</label>
              <textarea
                id={`notes-${concern.id}`}
                placeholder="Add notes about the resolution..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                disabled={isResolving}
              />
            </div>
            
            {/* Resolution Description */}
            {selectedResolution && (
              <div className="resolution-hint">
                {RESOLUTION_OPTIONS.find(o => o.value === selectedResolution)?.description}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

function ConcernsPanel({ patientId, onConcernResolved, onDiscussWithLuna }) {
  // State
  const [concerns, setConcerns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // UI State
  const [expandedId, setExpandedId] = useState(null)
  const [severityFilter, setSeverityFilter] = useState(null)
  const [showResolved, setShowResolved] = useState(false)
  
  // ============================================================================
  // Data Fetching
  // ============================================================================
  
  /**
   * Load concerns for the patient
   */
  const loadConcerns = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Get all pending concerns and filter by patient
      const data = await mdApi.getPendingConcerns(severityFilter)
      
      // Filter by patient ID if provided
      const patientConcerns = patientId 
        ? data.filter(c => c.patient_id === patientId)
        : data
      
      setConcerns(patientConcerns || [])
    } catch (err) {
      console.error('Error loading concerns:', err)
      setError('Failed to load concerns. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [patientId, severityFilter])
  
  // Initial load
  useEffect(() => {
    loadConcerns()
  }, [loadConcerns])
  
  // ============================================================================
  // Event Handlers
  // ============================================================================
  
  /**
   * Toggle concern expansion
   */
  const handleToggleExpand = (concernId) => {
    setExpandedId(prev => prev === concernId ? null : concernId)
  }
  
  /**
   * Resolve a concern
   * Note: concernId here is the string concern_id (e.g., "CONCERN-xxx") from the API
   */
  const handleResolveConcern = async (concernId, resolution) => {
    await mdApi.resolveConcern(concernId, resolution)
    
    // Remove from local state - filter by concern_id since that's what we passed to the API
    setConcerns(prev => prev.filter(c => (c.concern_id || c.id) !== concernId))
    
    // Notify parent if callback provided
    if (onConcernResolved) {
      onConcernResolved(concernId, resolution)
    }
  }
  
  /**
   * View source conversation
   */
  const handleViewConversation = (conversationId) => {
    // In a real app, this would navigate to the conversation
    // or open a modal with the conversation details
    console.log('View conversation:', conversationId)
  }
  
  /**
   * Refresh concerns
   */
  const handleRefresh = () => {
    loadConcerns()
  }
  
  // ============================================================================
  // Filtered and Sorted Concerns
  // ============================================================================
  
  const sortedConcerns = useMemo(() => {
    // Sort by severity (urgent first) then by date (newest first)
    const severityOrder = { urgent: 0, high: 1, medium: 2, low: 3 }
    return [...concerns].sort((a, b) => {
      const orderDiff = (severityOrder[a.severity] || 4) - (severityOrder[b.severity] || 4)
      if (orderDiff !== 0) return orderDiff
      return new Date(b.created_at) - new Date(a.created_at)
    })
  }, [concerns])
  
  // ============================================================================
  // Concern Counts by Severity
  // ============================================================================
  
  const concernCounts = useMemo(() => {
    return {
      urgent: concerns.filter(c => c.severity === 'urgent').length,
      high: concerns.filter(c => c.severity === 'high').length,
      medium: concerns.filter(c => c.severity === 'medium').length,
      low: concerns.filter(c => c.severity === 'low').length,
      total: concerns.length,
    }
  }, [concerns])
  
  // ============================================================================
  // Render
  // ============================================================================
  
  if (loading) {
    return (
      <div className="concerns-panel loading">
        <div className="spinner"></div>
        <p>Loading concerns...</p>
      </div>
    )
  }
  
  return (
    <div className="concerns-panel">
      {/* Panel Header */}
      <div className="panel-header">
        <h3>
          <AlertCircle size={20} />
          Active Concerns
          {concernCounts.total > 0 && (
            <span className="concern-total-badge">{concernCounts.total}</span>
          )}
        </h3>
        
        <div className="panel-controls">
          <button 
            className="btn-refresh"
            onClick={handleRefresh}
            disabled={loading}
            aria-label="Refresh concerns"
          >
            <RefreshCw size={14} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>
      
      {/* Severity Filter Pills */}
      <div className="severity-filters">
        <button
          className={`severity-pill ${severityFilter === null ? 'active' : ''}`}
          onClick={() => setSeverityFilter(null)}
        >
          All ({concernCounts.total})
        </button>
        {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
          <button
            key={key}
            className={`severity-pill ${severityFilter === key ? 'active' : ''}`}
            style={{ '--pill-color': config.color }}
            onClick={() => setSeverityFilter(key)}
          >
            <span className="pill-icon">{config.icon}</span>
            {config.label} ({concernCounts[key]})
          </button>
        ))}
      </div>
      
      {/* Error State */}
      {error && (
        <div className="concerns-error">
          <AlertTriangle size={20} />
          <span>{error}</span>
          <button onClick={handleRefresh}>Retry</button>
        </div>
      )}
      
      {/* Concerns List */}
      {sortedConcerns.length === 0 ? (
        <div className="no-concerns">
          <Check size={32} />
          <p>No active concerns for this patient</p>
        </div>
      ) : (
        <div className="concerns-list">
          {sortedConcerns.map(concern => (
            <ConcernCard
              key={concern.id}
              concern={concern}
              isExpanded={expandedId === concern.id}
              onToggleExpand={handleToggleExpand}
              onResolve={handleResolveConcern}
              onViewConversation={handleViewConversation}
              onDiscussWithLuna={onDiscussWithLuna}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default ConcernsPanel